import { FC, useEffect, useMemo, useRef } from "react";
import Highcharts from "highcharts";
import { Stack } from "@mui/material";
import { HighChartPieChartLegends } from "./components/HighChartPieChartLegends";
import { HighChartsCommonProps } from "../common/utils/types/common";
import { useHighchartsContext } from "../common/components/HighchartsProvider";
import { highChartColors } from "../common/utils/constants/chartColors";
import { chartDefaultConfigs } from "../common/utils/constants/chartDefaultConfigs";
import { plotOptionsSeries } from "../common/utils/plot-options/plotOptionsSeries";
import { getTooltipOptions } from "../common/utils/tooltip/getTooltipOptions";

interface HighchartsPieChartProps extends HighChartsCommonProps {
  showLegends?: boolean;
  showDataLabels?: boolean;
}

export const HighchartsPieChart: FC<HighchartsPieChartProps> = ({
  loading,
  chartData,
  showLegends,
  showDataLabels,
  tooltipMarkupGenerator,
  maxHeight = 370,
  tooltipOptions,
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const { chartApi, setChartApi } = useHighchartsContext();

  const data = useMemo(
    () =>
      chartData.series.map((item) => ({
        name: item.name,
        y: item.data.reduce((acc, cur) => (acc ?? 0) + (cur?.y ?? 0), 0) ?? 0,
      })),
    [chartData.series],
  );

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    // @ts-expect-error  | Something wrong with types, this is waiting for "Options" but it should be an "Element"
    const chart = Highcharts.chart(chartRef.current, {
      chart: {
        type: "pie",
      },

      colors: highChartColors,

      title: { text: "" },

      series: [
        {
          name: "Cost",
          colorByPoint: true,
          data: data,
        },
      ],

      tooltip: tooltipMarkupGenerator
        ? getTooltipOptions({
            tooltipMarkupGenerator,
            tooltipOptions,
          })
        : { enabled: false },

      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: "pointer",

          showInLegend: false,
          borderWidth: 0,
          borderRadius: 0,
          center: ["60%", "50%"],
          dataLabels: {
            enabled: showDataLabels,
            format: "{point.percentage:.1f}%",
            distance: -35,
            style: {
              color: "white",
              fontWeight: "bold",
              textOutline: "none",
            },
          },
        },
        series: plotOptionsSeries,
      },

      ...chartDefaultConfigs,
    });

    setChartApi(chart);

    return () => {
      chart.destroy();
    };
  }, [
    chartData,
    setChartApi,
    tooltipOptions,
    tooltipMarkupGenerator,
    data,
    showDataLabels,
  ]);

  useEffect(() => {
    if (!chartApi) {
      return;
    }

    if (loading) {
      chartApi?.showLoading?.();
    } else {
      chartApi?.hideLoading?.();
    }
  }, [chartApi, loading]);

  return (
    <Stack direction="row" width="100%" height="100%" overflow="auto">
      <Stack ref={chartRef} flex={1.5} maxHeight={maxHeight} />

      {!loading && showLegends && <HighChartPieChartLegends data={data} />}
    </Stack>
  );
};
